import { motion } from "framer-motion";
import { Camera, Mic, Headphones, Clapperboard, Crosshair, Radio, UserCheck, Users, MessageSquare, Video } from "lucide-react";

const ROWS = [
  { n: "01", title: "Live studio practice", text: "Handle real cameras, lights, grip and audio gear." },
  { n: "02", title: "Project-based briefs", text: "Create work that belongs in your showreel." },
  { n: "03", title: "Industry feedback", text: "Learn through honest reviews and guided iterations." },
  { n: "04", title: "Small learning cohorts", text: "More time on gear. More personal direction." },
  { n: "05", title: "Lime Studios access", text: "Train on our pro-grade studio floor — the latest cinema cameras and shooting gear, in your hands from week one." },
];

function Monitor() {
  return (
    <div className="bg-lima-navy border border-black/20 p-6 md:p-10 h-full min-h-[320px] md:min-h-[420px] flex flex-col" data-testid="studio-panel">
      <p className="font-mono2 text-[11px] tracking-[0.3em] text-white/50">STUDIO SESSION 07</p>
      <div className="flex-1 grid place-items-center py-10">
        <div className="relative w-full max-w-md aspect-video bg-black/60 border border-white/10 overflow-hidden">
          <video
            ref={(el) => el && el.play().catch(() => {})}
            src="https://videos.pexels.com/video-files/28305474/12356347_640_360_60fps.mp4"
            poster="https://images.unsplash.com/photo-1611784728558-6c7d9b409cdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxjaW5lbWF0b2dyYXBoZXIlMjBmaWxtJTIwc2V0fGVufDB8fHx8MTc4ODAzMDI1Nnww&ixlib=rb-4.1.0&q=85"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-70 saturate-[.6]"
          />
          <span className="absolute top-3 left-3 font-mono2 text-[10px] tracking-[0.25em] text-white/80 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-lima-red animate-rec" /> LIVE / CAM A
          </span>
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lima-blue/80" aria-hidden="true">
            <Crosshair size={40} strokeWidth={1} />
          </span>
          {["top-3 right-3 border-t border-r", "bottom-3 left-3 border-b border-l", "bottom-3 right-3 border-b border-r"].map((c) => (
            <span key={c} className={`absolute w-5 h-5 border-lima-blue/70 ${c}`} aria-hidden="true" />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-6 text-white/40">
        <Camera size={17} /> <Mic size={17} /> <Headphones size={17} /> <Clapperboard size={17} />
      </div>
    </div>
  );
}

export default function SetClassroom() {
  return (
    <section id="how" className="bg-lima-cream text-lima-ink px-5 md:px-10 py-14 md:py-32 border-b border-black/10" data-testid="set-classroom-section">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, x: -30, filter: "blur(14px)" }}
          whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <Monitor />
        </motion.div>
        <div className="lg:col-span-7">
          <p className="font-mono2 text-xs tracking-[0.25em] text-black font-bold mb-4">LESS THEORY. MORE MAKING.</p>
          <motion.h2
            initial={{ opacity: 0, y: 40, scale: 1.03, filter: "blur(16px)" }}
            whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] mb-6"
          >
            The set is your
            <br />
            <span className="font-serifit font-normal text-[#3a3a36]">classroom.</span>
          </motion.h2>
          <p className="max-w-lg text-sm md:text-base text-black/60 leading-relaxed mb-10">
            You will learn by planning, shooting, reviewing, failing safely, fixing fast — and doing it again with
            better intention.
          </p>
          <div className="divide-y divide-black/10 border-t border-b border-black/10">
            {ROWS.map((r, i) => (
              <motion.div
                key={r.n}
                initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="flex items-baseline gap-6 py-6"
                data-testid={`set-classroom-point-${i}`}
              >
                <span className="font-mono2 text-xs font-bold text-lima-blue shrink-0">{r.n}</span>
                <div>
                  <p className="font-display font-bold text-lg md:text-xl tracking-tight">{r.title}</p>
                  <p className="mt-1 text-sm text-black/55">{r.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
